import { dropTask, all } from 'ember-concurrency';
import Footer from 'tio-ui/components/footer';
import Modal from 'tio-ui/components/modal';
import { htmlSafe } from '@ember/template';
import * as v from 'valibot';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { modifier } from 'ember-modifier';
import { trackedArray } from '@ember/reactive/collections';
import Component from '@glimmer/component';
import errorsForField from 'tio-common/helpers/errors-for-field';
import Input from 'tio-ui/components/input';
import TioDialogDocumentAsset from 'tio-common/components/tio/dialog/document-asset';
import type { IntlService } from 'ember-intl';
import type { ConditionExtension as ConditionModel } from 'tio-data/data/resources/condition.ext';
import type { EmailAddressExtension as EmailAddressModel } from 'tio-data/data/resources/email-address.ext';
import type Errors from 'tio-common/types/errors';
import type Owner from '@ember/owner';
import type PreRegisterService from 'tio-employee/services/pre-register';
import type RouterService from '@ember/routing/router';
import type SessionContextService from 'tio-employee/services/session-context';
// This is for types. Anything we have custom may show an error.
import type { Store as StoreService } from '@warp-drive/core';
import { saveRecord } from 'tio-data/requests/save-record';
import { contains } from 'tio-ui/utilities';
import Checkbox from 'tio-ui/components/checkbox';
import PhoneInput, { emptyPhone } from 'tio-ui/components/phone-input';
import type { PhoneValue } from 'tio-ui/components/phone-input';
import ProgressBar from 'tio-ui/components/progress-bar';
import PhoneVerificationFlow from 'tio-employee/components/app/phone-verification/flow';
import type FeaturesService from 'tio-common/services/features';
import type PhoneCaptureService from 'tio-employee/services/phone-capture';
import type { Agreement as AgreementModel } from 'tio-data/data/resources/agreement.type';
import type { UserExtension as UserModel } from 'tio-data/data/resources/user.ext';

/** Step 2 replaces the footer that held focus, and Modal only focuses on first open. */
const focusOnInsert = modifier((element: HTMLElement) => {
  element.focus();
});

const postAuthRegisterFormSchema = v.object({
  didAcceptAllTerms: v.pipe(
    v.boolean('The terms and conditions must be accepted.'),
    v.check((val) => val === true, 'The terms and conditions must be accepted.')
  ),
});

export interface AuthRegisterPostAuthTermsDialogSignature {
  Args: {
    primaryEmailAddress?: EmailAddressModel;
    didSave?: () => void;
  };
}

export default class AuthRegisterPostAuthTermsDialogComponent extends Component<AuthRegisterPostAuthTermsDialogSignature> {
  @service declare intl: IntlService;
  @service declare store: StoreService;
  @service declare router: RouterService;
  @service declare preRegister: PreRegisterService;
  @service declare sessionContext: SessionContextService;
  @service declare features: FeaturesService;
  @service declare phoneCapture: PhoneCaptureService;

  @tracked hasSubmitted = false;
  @tracked submitError?: Errors;
  @tracked email = '';
  @tracked didAgreeAppTermsAndPrivacy = false;
  @tracked showingTermsOfUse = false;
  @tracked showingPrivacyPolicy = false;
  @tracked phone: PhoneValue = emptyPhone();
  /** Set once step 1 is saved and a number needs confirming; there is no way back. */
  @tracked private stepOverride?: 'confirm';

  acceptedCustomTerms: ConditionModel[] = trackedArray([]);

  constructor(owner: Owner, args: AuthRegisterPostAuthTermsDialogSignature['Args']) {
    super(owner, args);

    if (this.args.primaryEmailAddress) {
      this.email = this.args.primaryEmailAddress.email;
    }

    // Pre-check the app terms checkbox if both are already accepted
    if (this.hasAcceptedTermsOfUse && this.hasAcceptedPrivacyPolicy) {
      this.didAgreeAppTermsAndPrivacy = true;
    }
  }

  get user(): UserModel {
    return this.sessionContext.user;
  }

  get isSsoRegistration() {
    return this.sessionContext.isOtherProviderLogin;
  }

  /** The surface is SSO first access; the same dialog also serves updated-terms acceptance. */
  get showPhoneCapture() {
    return this.isSsoRegistration && this.features.can('phoneCapture.surfaces.ssoModal');
  }

  get step() {
    return this.stepOverride ?? 'details';
  }

  get phoneForPayload() {
    return this.phone.e164 ?? (this.phone.national || null);
  }

  /** Whether step 2 is owed: only a number a code can reach, on the capture surface. */
  get phoneEntered() {
    return this.showPhoneCapture && !!this.phone.e164;
  }

  get phoneForFlow() {
    return this.phoneForPayload ?? undefined;
  }

  setPhoneValue = (phone: PhoneValue) => {
    this.phone = phone;
  };

  /** Verified or deferred, prompting is over and the dialog may finally hand back. */
  finishPhoneStep = () => {
    this.args.didSave?.();
  };

  get needsEmailInput() {
    return this.isSsoRegistration && !this.args.primaryEmailAddress?.email;
  }

  get termsOfUse() {
    return this.preRegister.termsOfUse;
  }

  get privacyPolicy() {
    return this.preRegister.privacyPolicy;
  }

  get termsOfUseContent() {
    return this.termsOfUse?.fullText;
  }

  get privacyPolicyContent() {
    return this.privacyPolicy?.fullText;
  }

  get hasAcceptedTermsOfUse() {
    return !!this.user?.usageAgreement;
  }

  get hasAcceptedPrivacyPolicy() {
    return !!this.user?.privacyAgreement;
  }

  get needsAppTermsCheckbox() {
    return !this.hasAcceptedTermsOfUse || !this.hasAcceptedPrivacyPolicy;
  }

  /** Custom terms the user has NOT yet agreed to */
  get customTerms(): ConditionModel[] {
    const allCustom = this.preRegister.customTerms || [];
    const agreedIds = new Set(
      // @ts-expect-error: relation typing
      (this.user?.customRegisterAgreements || []).map((a: AgreementModel) => a.condition?.id)
    );
    return allCustom.filter((c) => !agreedIds.has(c.id));
  }

  get didAcceptAllCustomTerms() {
    return this.acceptedCustomTerms.length === this.customTerms.length;
  }

  get didAcceptAllTerms() {
    return this.didAgreeAppTermsAndPrivacy && this.didAcceptAllCustomTerms;
  }

  /** Only the conditions the user still needs to agree to */
  get allConditionsToAccept(): ConditionModel[] {
    const missing: ConditionModel[] = [];
    if (!this.hasAcceptedTermsOfUse && this.termsOfUse) {
      missing.push(this.termsOfUse);
    }
    if (!this.hasAcceptedPrivacyPolicy && this.privacyPolicy) {
      missing.push(this.privacyPolicy);
    }
    missing.push(...this.customTerms);
    return missing;
  }

  get formModel() {
    return {
      email: this.email,
      didAcceptAllTerms: this.didAcceptAllTerms,
    };
  }

  get formSchema() {
    if (this.needsEmailInput) {
      return v.object({
        didAcceptAllTerms: v.pipe(
          v.boolean('The terms and conditions must be accepted.'),
          v.check((val) => val === true, 'The terms and conditions must be accepted.')
        ),
        email: v.pipe(
          v.string('Email is required'),
          v.minLength(1, '* Email is required'),
          v.email('Please enter a valid email address')
        ),
      });
    }
    return postAuthRegisterFormSchema;
  }

  get formValidationErrors() {
    const schema = this.formSchema;

    try {
      v.parse(schema, this.formModel);
    } catch (err) {
      if (err instanceof v.ValiError) {
        return err.issues || [];
      }
      return [];
    }
    return [];
  }

  get serverErrorMessages() {
    const genericErrorMessage = this.intl.t('error_occurred');
    if (this.submitError?.errors) {
      return this.submitError.errors.map((err) => {
        return err?.detail || genericErrorMessage;
      });
    } else if (this.submitError) {
      return [genericErrorMessage];
    }
    return [];
  }

  didChangeAcceptanceOfCustomTerm = (condition: ConditionModel, value: boolean) => {
    if (value) {
      this.acceptedCustomTerms.push(condition);
    } else {
      const index = this.acceptedCustomTerms.indexOf(condition);
      if (index !== -1) {
        this.acceptedCustomTerms.splice(index, 1);
      }
    }
  };

  showDialog = (type: string, event: Event) => {
    event.preventDefault();
    event.stopPropagation();
    switch (type) {
      case 'terms':
        this.showingTermsOfUse = true;
        break;
      case 'privacy':
        this.showingPrivacyPolicy = true;
        break;
    }
  };

  submitTask = dropTask(async () => {
    delete this.submitError;
    this.hasSubmitted = true;
    if (this.formValidationErrors.length) {
      return;
    }
    try {
      await this.saveFormValues.perform();
      this.hasSubmitted = false;

      // Handing back now would let sessionContext.load() flip needsToAcceptAppTerms and
      // unmount this dialog with the number still unconfirmed.
      if (this.phoneEntered) {
        this.stepOverride = 'confirm';
        return;
      }

      await this.args.didSave?.();
    } catch (e) {
      this.submitError = e;
      console.error(e);
    }
  });

  saveFormValues = dropTask(async () => {
    const user = this.sessionContext.user;
    const agreements = this.allConditionsToAccept.map((condition) => {
      return this.store.createRecord<AgreementModel>('agreement', {
        user: user,
        condition: condition,
      });
    });

    const promises = agreements.map((agreement) => {
      return saveRecord(this.store, agreement);
    });
    // SSO sign-up only: without the email field there is nothing to save, and a failed side-write must not block dismissal.
    if (this.needsEmailInput) {
      const emailAddress =
        this.args.primaryEmailAddress ||
        this.store.createRecord<EmailAddressModel>('email-address', {
          contactType: 'PRIMARY',
          user: user,
        });
      emailAddress.email = this.email;
      promises.push(saveRecord(this.store, emailAddress));
    }
    const saved = await all(promises);
    return saved;
  });

  <template>
    <Modal
      @isOpen={{true}}
      @onClose={{this.submitTask.perform}}
      @size={{if this.showPhoneCapture "lg" "xl"}}
      @allowCloseButton={{false}}
    >
      <:body>
        {{#if (eq this.step "confirm")}}
          <div class="tio-col-loose" data-test-sso-phone-step>
            {{! Two adjacent bars plus a count: ProgressBar has no multi-segment mode, and
                a segments variant would be a wider change than one surface warrants. }}
            <div class="tio-row items-center gap-2">
              <ProgressBar
                @value={{100}}
                @size="sm"
                @color="violet"
                class="grow"
                aria-hidden="true"
              />
              {{! Part-filled, not full: this step is under way. It never reaches 100 -- a
                  verified code hands back to the caller in the same tick. }}
              <ProgressBar
                @value={{50}}
                @size="sm"
                @color="violet"
                class="grow"
                aria-hidden="true"
              />
              <span class="tio-text-xs-medium tio-fg-muted" data-test-sso-step-counter>
                {{t "login.sso_capture.step_counter" current=2 total=2}}
              </span>
            </div>

            <h2
              class="tio-text-2xl-bold"
              tabindex="-1"
              data-test-sso-confirm-heading
              {{focusOnInsert}}
            >{{t "login.sso_capture.confirm_number"}}</h2>

            <PhoneVerificationFlow
              @surface="ssoModal"
              @phoneNumber={{this.phoneForFlow}}
              @country={{this.phone.country}}
              @autoSend={{true}}
              @size="md"
              @onVerified={{this.finishPhoneStep}}
              @onDeferred={{this.finishPhoneStep}}
            />
          </div>
        {{else}}
          <div class="tio-stack-center">
            {{#if this.showPhoneCapture}}
              <h2 class="tio-text-4xl-bold mb-4" data-test-sso-capture-title>{{t
                  "login.sso_capture.title"
                }}</h2>
              <p class="tio-text-xl-normal mb-6 text-center">{{t "login.sso_capture.subtitle"}}</p>
            {{else}}
              <h2 class="text-4xl mb-4">
                {{if this.needsEmailInput (t "login.sign_up") (t "login.terms_of_service_heading")}}
              </h2>

              <h2 class="text-xl mb-6 text-center">
                {{if
                  this.needsEmailInput
                  (t "login.enroll_in_tuition_to_access")
                  (t "login.please_accept_the_terms")
                }}
              </h2>
            {{/if}}
          </div>

          <form class="tio-stack-center my-4">
            {{#if this.needsEmailInput}}
              <div class="max-w-xl">
                <Input
                  data-test-id="email-input"
                  @label={{t "login.email"}}
                  @value={{this.email}}
                  @onChange={{fn (mut this.email)}}
                  @errors={{errorsForField "email" schemaErrors=this.formValidationErrors}}
                />
                <div class="text-sm tio-fg-muted my-2">
                  {{t "login.please_use_email_for_notifications"}}
                </div>
              </div>
            {{/if}}

            {{#if this.showPhoneCapture}}
              <div class="max-w-xl">
                <PhoneInput
                  @testHook="sso-phone"
                  @label={{t "login.mobile"}}
                  @value={{this.phone}}
                  @onChange={{this.setPhoneValue}}
                  @countries={{this.phoneCapture.regions}}
                  @hint={{t "login.sso_capture.mobile_hint"}}
                  @isOptional={{true}}
                />
              </div>
            {{/if}}

            <div class="my-2">
              {{#if this.needsAppTermsCheckbox}}
                <div class="flex flex-row items-start my-2">
                  <Checkbox
                    data-test-id="app-terms-checkbox"
                    @size="lg"
                    @checked={{this.didAgreeAppTermsAndPrivacy}}
                    @onChange={{fn (mut this.didAgreeAppTermsAndPrivacy)}}
                    aria-labelledby="appTermsAndPrivacy"
                  />
                  <div id="appTermsAndPrivacy" class="ml-1">
                    {{t "login.i_have_read_and_agree_to_tuitions"}}
                    <button
                      class="app-text-link"
                      type="button"
                      {{on "click" (fn this.showDialog "terms")}}
                    >
                      {{t "login.terms_of_service"}}
                    </button>
                    {{t "and"}}
                    <button
                      class="app-text-link"
                      type="button"
                      {{on "click" (fn this.showDialog "privacy")}}
                    >
                      {{t "login.privacy_policy"}}.
                    </button>
                  </div>
                </div>
              {{/if}}

              {{#each this.customTerms as |condition|}}
                <div class="flex flex-row items-start my-2">
                  <Checkbox
                    data-test-id="custom-terms-checkbox"
                    @size="lg"
                    @checked={{contains this.acceptedCustomTerms condition}}
                    @onChange={{fn this.didChangeAcceptanceOfCustomTerm condition}}
                    aria-labelledby={{condition.id}}
                  />
                  <div id={{condition.id}} class="ml-1 tio-prose-content">
                    {{htmlSafe condition.fullText}}
                  </div>
                </div>
              {{/each}}
            </div>

            <div role="alert" class="text-sm tio-fg-danger" data-test-terms-dialog-server-error>
              {{#each this.serverErrorMessages as |message|}}
                <div>{{message}}</div>
              {{/each}}
            </div>
          </form>
        {{/if}}
      </:body>
      <:footer>
        {{! The flow owns its own actions on step 2, so this footer must not double them. }}
        {{#unless (eq this.step "confirm")}}
          <Footer
            @isRunning={{this.submitTask.isRunning}}
            @onSubmit={{this.submitTask.perform}}
            @submitDisabled={{if this.formValidationErrors.length true false}}
            @submitText={{t "login.agree_text"}}
          />
        {{/unless}}
      </:footer>
    </Modal>

    <TioDialogDocumentAsset
      @title="Privacy Policy"
      {{! @glint-expect-error: should privacy be optional? }}
      @assetName={{this.privacyPolicy.staticJsonAssetName}}
      @content={{this.privacyPolicyContent}}
      @show={{this.showingPrivacyPolicy}}
      @onCancel={{fn (mut this.showingPrivacyPolicy) false}}
    />

    <TioDialogDocumentAsset
      @title="Terms of Service"
      {{! @glint-expect-error: should privacy be optional? }}
      @assetName={{this.termsOfUse.staticJsonAssetName}}
      @content={{this.termsOfUseContent}}
      @show={{this.showingTermsOfUse}}
      @onCancel={{fn (mut this.showingTermsOfUse) false}}
    />
  </template>
}
